import type {ReactElement} from 'react';
import {EmailMakerInputSuggestionsDropdown} from '@Components/email-maker-input-suggestions-dropdown/email-maker-input-suggestions-dropdown';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import type {EmailMakerSocialLinkInputSuggestionsProps} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown.types';
import {useAppSelector} from '@/hooks';
import type {InputSuggestionsDropdownProps, SuggestionItems} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.types';
import type {InputFieldProps} from '@Components/input-field-v2';
import {GA4EventName} from '@Libraries/ga-events';

export function EmailMakerSocialLinkSuggestionsDropdown({...props}: EmailMakerSocialLinkInputSuggestionsProps): ReactElement<any> {
  const socialLinkSuggestions = useAppSelector((state) => {
    return state.emailMakerSocialLinkSuggestionsDropdown.socialLinkSuggestions;
  });

  const getDataIdAttrForHandlingSocialLinkUpdate = (): string => {
    return props.platform;
  };

  const handleInputFocus = (value: InputType): void => {
    const dataId = getDataIdAttrForHandlingSocialLinkUpdate();
    props.socialLinkInputCustomHandlers?.onInputFocus?.(dataId, value.toString());
  };

  const handleInputBlur = (value: InputType): void => {
    const dataId = getDataIdAttrForHandlingSocialLinkUpdate();
    props.socialLinkInputCustomHandlers?.onInputBlur?.(dataId, value.toString());
  };

  const handleSuggestionSelection = (value: string): void => {
    handleInputBlur(value as InputType);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.BUSINESS_PROFILES_EMAIL_EDITOR_SOCIAL_LINK);
  };

  const getSocialLinkSuggestionsForPlatform = (): SuggestionItems => {
    if (!socialLinkSuggestions) {
      return [];
    }

    const businessProfileSuggestions = socialLinkSuggestions.businessProfiles?.[props.platform] || [];
    const connectedAccountSuggestions = socialLinkSuggestions.connectedAccounts?.[props.platform] || [];

    return [...businessProfileSuggestions, ...connectedAccountSuggestions];
  };

  const getInputFieldProps = (): Partial<InputFieldProps> => {
    return {
      ...props.inputFieldProps,
      input: getInputValue(),
      onInputBlur: handleInputBlur,
      onInputFocus: handleInputFocus,
    };
  };

  const getInputValue = (): InputType => {
    const inputValue = props.inputFieldProps?.input ?? '';
    const isEmptyInput = !inputValue;

    if (!isEmptyInput) {
      return inputValue;
    }

    const platformHasBusinessProfileSuggestion = socialLinkSuggestions.businessProfiles?.[props.platform];

    if (!platformHasBusinessProfileSuggestion) {
      return inputValue;
    }

    const businessProfileSuggestion = socialLinkSuggestions.businessProfiles?.[props.platform][0].primaryTextValue;

    return businessProfileSuggestion ?? inputValue;
  };

  const getInputSuggestionDropdownProps = (): Partial<InputSuggestionsDropdownProps> => {
    return {
      ...props.inputSuggestionsDropdownProps,
      primarySuggestionsList: getSocialLinkSuggestionsForPlatform(),
      onSuggestionSelect: handleSuggestionSelection,
    };
  };

  return (
    <EmailMakerInputSuggestionsDropdown
      dropdownId={`${props.platform}-input-suggestion-dropdown`}
      inputId={props.platform}
      inputSuggestionsDropdownProps={getInputSuggestionDropdownProps()}
      inputFieldProps={getInputFieldProps()}
    />
  );
}
